import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { MyceliumText, PulsingNode, CountUp, StaggerContainer, StaggerItem, RevealWrapper, SectionConnector, organicEase, FloatingWrapper } from '../components/MyceliumAnimations';
export function HomeThreeTruths() {
  return <div className="min-h-screen bg-black" data-id="element-1046">
      {/* Hero - Triptych Preview */}
      <section aria-label="Vitality Venture Fund — Investing in nature-inspired innovation" className="relative h-screen flex items-center justify-center overflow-hidden" data-id="element-1047">
        {/* Press Banner - Consolidated at top */}
        <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5,
        duration: 0.6
      }} className="absolute top-24 left-0 right-0 z-20 px-4" data-id="element-1048">
          <div className="max-w-7xl mx-auto" data-id="element-1049">
            <div className="bg-black/40 backdrop-blur-md border border-vitalityTeal/20 rounded-full px-4 sm:px-6 py-3 inline-flex items-center gap-2 sm:gap-4 mx-auto flex-wrap justify-center" data-id="element-1050">
              <span className="text-gray-400 text-xs uppercase tracking-wider" data-id="element-1051">
                As featured in
              </span>
              <div className="flex items-center gap-2 sm:gap-4 flex-wrap justify-center" data-id="element-1052">
                <a href="https://www.ibtimes.com/vitality-vision-natalya-wallins-full-circle-journey-investing-nature-inspired-innovation-3779469" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-vitalityTeal transition-colors text-xs font-medium py-1" data-id="element-1053">
                  International Business Times
                </a>
                <span className="text-gray-600 hidden sm:inline" data-id="element-1054">•</span>
                <a href="https://ceoweekly.com/the-next-big-wave-in-tech-is-ancient-how-natalya-wallin-and-vitality-are-betting-on-nature-to-solve-modern-problems/" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-vitalityTeal transition-colors text-xs font-medium py-1" data-id="element-1055">
                  CEO Weekly
                </a>
                <span className="text-gray-600 hidden sm:inline" data-id="element-1056">•</span>
                <a href="https://www.usatoday.com/story/special/contributor-content/2025/11/03/its-time-to-rethink-what-and-who-we-fund-says-vitality-founder-natalya-wallin/87064743007/" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-vitalityTeal transition-colors text-xs font-medium py-1" data-id="element-1057">
                  USA Today
                </a>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Three images side by side */}
        <div className="absolute inset-0 grid grid-cols-3" data-id="element-1058">
          {[{
          src: 'https://cdn.magicpatterns.com/uploads/68H7NDRoLcn1FKiHRZijKp/10_SunsettingCanyon.jpg',
          alt: 'Sunset over ancient canyon landscape representing endurance and long-term thinking'
        }, {
          src: 'https://cdn.magicpatterns.com/uploads/c6jzDkT8Z7Z2KhqiesKc4w/11_MoreViews.jpg',
          alt: 'Expansive natural vista symbolizing regenerative systems and growth'
        }, {
          src: 'https://cdn.magicpatterns.com/uploads/qvkzqkkci4tMqBQJeNxkje/3_NatSnow.jpg',
          alt: 'Natalya Wallin in winter wilderness demonstrating conviction and resilience'
        }].map((img, i) => <motion.div key={i} className="relative overflow-hidden" initial={{
          opacity: 0,
          scale: 1.1
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: i * 0.2,
          duration: 1.2,
          ease: organicEase
        }} data-id="element-1059">
              <img src={img.src} alt={img.alt} className="w-full h-full object-cover" data-id="element-1060" />
              <motion.div className="absolute inset-0 bg-black/60" initial={{
            opacity: 0.8
          }} animate={{
            opacity: 0.6
          }} transition={{
            delay: 0.5 + i * 0.2,
            duration: 1
          }} data-id="element-1061" />
            </motion.div>)}
        </div>

        {/* Floating decorative nodes */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-1062">
          <FloatingWrapper className="absolute top-1/4 left-[15%]" amplitude={12} duration={5} data-id="element-1063">
            <PulsingNode size={6} delay={0.5} intensity="low" data-id="element-1064" />
          </FloatingWrapper>
          <FloatingWrapper className="absolute top-1/3 left-[50%]" amplitude={15} duration={4.5} data-id="element-1065">
            <PulsingNode size={8} delay={1} intensity="medium" data-id="element-1066" />
          </FloatingWrapper>
          <FloatingWrapper className="absolute top-1/4 right-[15%]" amplitude={10} duration={5.5} data-id="element-1067">
            <PulsingNode size={5} delay={1.5} intensity="low" data-id="element-1068" />
          </FloatingWrapper>
        </div>

        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto" data-id="element-1069">
          <motion.h1 className="text-6xl md:text-8xl font-bold text-white mb-8" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.3,
          duration: 0.8
        }} data-id="element-1070">
            <MyceliumText as="span" delay={0.3} staggerChildren={0.04} data-id="element-1071">
              Investing in a world of Vitality
            </MyceliumText>
          </motion.h1>

          <RevealWrapper delay={0.4} data-id="element-1072">
            <p className="text-xl md:text-2xl text-vitalityTeal" data-id="element-1073">
              Fueling startups built on nature's intelligence
            </p>
          </RevealWrapper>
        </div>
      </section>

      <SectionConnector data-id="element-1074" />

      {/* Section Title for Three Truths */}
      <section className="py-16 bg-black" data-id="element-1075">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-1076">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-1077">
            <MyceliumText as="span" staggerChildren={0.04} data-id="element-1078">
              Three truths shape how we invest to drive results that matter
            </MyceliumText>
          </motion.h2>
        </div>
      </section>

      <SectionConnector data-id="element-1079" />

      {/* Truth 1 - Nature Endures */}
      <article aria-label="First Truth: What Endures, Matters" className="min-h-screen flex items-center relative overflow-hidden" data-id="element-1080">
        <div className="absolute inset-0" data-id="element-1081">
          <motion.img src="https://cdn.magicpatterns.com/uploads/68H7NDRoLcn1FKiHRZijKp/10_SunsettingCanyon.jpg" alt="Ancient canyon at sunset — billions of years of geological endurance" className="w-full h-full object-cover" initial={{
          scale: 1.1
        }} whileInView={{
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 1.5,
          ease: organicEase
        }} data-id="element-1082" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" data-id="element-1083" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-id="element-1084">
          <div className="max-w-2xl" data-id="element-1085">
            <RevealWrapper direction="left" data-id="element-1086">
              <div className="flex items-center gap-4 mb-6" data-id="element-1087">
                <motion.div className="w-12 h-12 rounded-full bg-vitalityTeal/20 border-2 border-vitalityTeal flex items-center justify-center" animate={{
                boxShadow: ['0 0 0px rgba(43, 199, 183, 0.3)', '0 0 20px rgba(43, 199, 183, 0.5)', '0 0 0px rgba(43, 199, 183, 0.3)']
              }} transition={{
                duration: 3,
                repeat: Infinity
              }} data-id="element-1088">
                  <span className="text-vitalityTeal font-bold text-xl" data-id="element-1089">
                    01
                  </span>
                </motion.div>
                <p className="text-vitalityTeal text-sm font-bold tracking-wider uppercase" data-id="element-1090">
                  First Truth
                </p>
              </div>
            </RevealWrapper>

            <motion.h2 className="text-5xl md:text-7xl font-bold text-white mb-8 leading-tight" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} data-id="element-1091">
              <MyceliumText as="span" staggerChildren={0.05} data-id="element-1092">
                What Endures, Matters
              </MyceliumText>
            </motion.h2>

            <StaggerContainer className="space-y-6 text-gray-300 text-lg leading-relaxed" staggerDelay={0.15} data-id="element-1093">
              <StaggerItem data-id="element-1094">
                <p data-id="element-1095">
                  Nature has survived billions of years of shocks, cycles, and
                  collapse. Most modern systems haven't survived a single
                  century without fragility.
                </p>
              </StaggerItem>
              <StaggerItem data-id="element-1096">
                <p data-id="element-1097">
                  Vitality invests in companies designed for resilience and
                  endurance, businesses built to last and thrive through
                  volatility. We favor long time horizons, durable advantages,
                  and founders willing to trade speed for staying power.
                </p>
              </StaggerItem>
              <StaggerItem data-id="element-1098">
                <p className="text-white font-semibold" data-id="element-1099">
                  Because what endures is what ultimately matters.
                </p>
              </StaggerItem>
            </StaggerContainer>

            <RevealWrapper delay={0.6} data-id="element-1100">
              <div className="mt-12 pt-8 border-t border-white/10" data-id="element-1101">
                <p className="text-white font-semibold mb-3" data-id="element-1102">
                  Long-Term Thinking
                </p>
                <p className="text-gray-400" data-id="element-1103">
                  3 billion years of evolution. Millions of years of geological
                  time. We measure success in decades, not quarters.
                </p>
              </div>
            </RevealWrapper>
          </div>
        </div>
      </article>

      <SectionConnector data-id="element-1104" />

      {/* Truth 2 - Nature Regenerates */}
      <article aria-label="Second Truth: What Regenerates, Compounds" className="min-h-screen flex items-center relative overflow-hidden bg-gradient-to-b from-black to-[#0a1f2e]" data-id="element-1105">
        <div className="absolute inset-0" data-id="element-1106">
          <motion.img src="https://cdn.magicpatterns.com/uploads/c6jzDkT8Z7Z2KhqiesKc4w/11_MoreViews.jpg" alt="Forest landscape demonstrating nature's regenerative capacity" className="w-full h-full object-cover opacity-50" initial={{
          scale: 1.1
        }} whileInView={{
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 1.5,
          ease: organicEase
        }} data-id="element-1107" />
          <div className="absolute inset-0 bg-gradient-to-l from-[#0a1f2e] via-[#0a1f2e]/90 to-transparent" data-id="element-1108" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-id="element-1109">
          <div className="max-w-2xl ml-auto" data-id="element-1110">
            <RevealWrapper direction="right" data-id="element-1111">
              <div className="flex items-center gap-4 mb-6" data-id="element-1112">
                <motion.div className="w-12 h-12 rounded-full bg-vitalityTeal/20 border-2 border-vitalityTeal flex items-center justify-center" animate={{
                boxShadow: ['0 0 0px rgba(43, 199, 183, 0.3)', '0 0 20px rgba(43, 199, 183, 0.5)', '0 0 0px rgba(43, 199, 183, 0.3)']
              }} transition={{
                duration: 3,
                repeat: Infinity,
                delay: 0.5
              }} data-id="element-1113">
                  <span className="text-vitalityTeal font-bold text-xl" data-id="element-1114">
                    02
                  </span>
                </motion.div>
                <p className="text-vitalityTeal text-sm font-bold tracking-wider uppercase" data-id="element-1115">
                  Second Truth
                </p>
              </div>
            </RevealWrapper>

            <motion.h2 className="text-5xl md:text-7xl font-bold text-white mb-8 leading-tight" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} data-id="element-1116">
              <MyceliumText as="span" staggerChildren={0.05} data-id="element-1117">
                What Regenerates, Compounds
              </MyceliumText>
            </motion.h2>

            <StaggerContainer className="space-y-6 text-gray-300 text-lg leading-relaxed" staggerDelay={0.15} data-id="element-1118">
              <StaggerItem data-id="element-1119">
                <p data-id="element-1120">
                  In nature, disruption doesn't end systems, it reshapes them.
                  Living systems regenerate, adapt, and grow stronger over time.
                </p>
              </StaggerItem>
              <StaggerItem data-id="element-1121">
                <p data-id="element-1122">
                  We invest in founders who apply this logic to the modern
                  world. Companies that work with natural systems rather than
                  against them, unlocking compounding benefits across
                  efficiency, resilience, and cost by combining biomimicry with
                  modern technology.
                </p>
              </StaggerItem>
            </StaggerContainer>

            <RevealWrapper delay={0.6} data-id="element-1123">
              <div className="mt-12 pt-8 border-t border-white/10" data-id="element-1124">
                <p className="text-white font-semibold mb-4" data-id="element-1125">
                  Three Investment Themes
                </p>
                <StaggerContainer className="grid grid-cols-3 gap-4" staggerDelay={0.1} data-id="element-1126">
                  {[{
                  label: 'Grow',
                  desc: 'Agriculture & Food'
                }, {
                  label: 'Build',
                  desc: 'Construction'
                }, {
                  label: 'Use',
                  desc: 'Materials'
                }].map((item, i) => <StaggerItem key={item.label} data-id="element-1127">
                      <motion.div className="text-center" whileHover={{
                    scale: 1.05,
                    textShadow: '0 0 10px rgba(43, 199, 183, 0.5)'
                  }} data-id="element-1128">
                        <p className="text-vitalityTeal font-bold mb-1" data-id="element-1129">
                          {item.label}
                        </p>
                        <p className="text-gray-500 text-sm" data-id="element-1130">{item.desc}</p>
                      </motion.div>
                    </StaggerItem>)}
                </StaggerContainer>
              </div>
            </RevealWrapper>
          </div>
        </div>
      </article>

      <SectionConnector data-id="element-1131" />

      {/* Truth 3 - Nature Requires Conviction */}
      <article aria-label="Third Truth: What Matters, Demands Conviction" className="min-h-screen flex items-center relative overflow-hidden bg-[#0a1f2e]" data-id="element-1132">
        <div className="absolute inset-0" data-id="element-1133">
          <motion.img src="https://cdn.magicpatterns.com/uploads/qvkzqkkci4tMqBQJeNxkje/3_NatSnow.jpg" alt="Natalya Wallin enduring winter conditions — conviction through adversity" className="w-full h-full object-cover opacity-60" initial={{
          scale: 1.1
        }} whileInView={{
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 1.5,
          ease: organicEase
        }} data-id="element-1134" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a1f2e] via-[#0a1f2e]/90 to-[#0a1f2e]/70" data-id="element-1135" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-id="element-1136">
          <div className="max-w-2xl" data-id="element-1137">
            <RevealWrapper direction="left" data-id="element-1138">
              <div className="flex items-center gap-4 mb-6" data-id="element-1139">
                <motion.div className="w-12 h-12 rounded-full bg-vitalityTeal/20 border-2 border-vitalityTeal flex items-center justify-center" animate={{
                boxShadow: ['0 0 0px rgba(43, 199, 183, 0.3)', '0 0 20px rgba(43, 199, 183, 0.5)', '0 0 0px rgba(43, 199, 183, 0.3)']
              }} transition={{
                duration: 3,
                repeat: Infinity,
                delay: 1
              }} data-id="element-1140">
                  <span className="text-vitalityTeal font-bold text-xl" data-id="element-1141">
                    03
                  </span>
                </motion.div>
                <p className="text-vitalityTeal text-sm font-bold tracking-wider uppercase" data-id="element-1142">
                  Third Truth
                </p>
              </div>
            </RevealWrapper>

            <motion.h2 className="text-5xl md:text-7xl font-bold text-white mb-8 leading-tight" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} data-id="element-1143">
              <MyceliumText as="span" staggerChildren={0.04} data-id="element-1144">
                What Matters, Demands Conviction
              </MyceliumText>
            </motion.h2>

            <StaggerContainer className="space-y-6 text-gray-300 text-lg leading-relaxed" staggerDelay={0.15} data-id="element-1145">
              <StaggerItem data-id="element-1146">
                <p data-id="element-1147">
                  The most important problems are rarely the easiest to solve.
                </p>
              </StaggerItem>
              <StaggerItem data-id="element-1148">
                <p data-id="element-1149">
                  Building nature-inspired companies requires patience,
                  technical depth, and the willingness to persist through
                  uncertainty. These aren't linear journeys, and they aren't for
                  founders or investors optimizing for quick wins.
                </p>
              </StaggerItem>
              <StaggerItem data-id="element-1150">
                <p data-id="element-1151">
                  Vitality backs founders with the conviction to build through
                  complexity and adapt. Our team knows that meaningful change
                  takes time.
                </p>
              </StaggerItem>
            </StaggerContainer>

            <RevealWrapper delay={0.6} data-id="element-1152">
              <div className="mt-12" data-id="element-1153">
                <Link to="/founders" data-id="element-1154">
                  <motion.div whileHover={{
                  scale: 1.02,
                  boxShadow: '0 0 30px rgba(43, 199, 183, 0.4)'
                }} transition={{
                  duration: 0.3
                }} className="inline-block rounded-lg" data-id="element-1155">
                    <Button variant="primary" data-id="element-1156">
                      Meet the Team
                      <ArrowRight className="w-4 h-4 ml-2" data-id="element-1157" />
                    </Button>
                  </motion.div>
                </Link>
              </div>
            </RevealWrapper>
          </div>
        </div>
      </article>

      <SectionConnector data-id="element-1158" />

      {/* Synthesis - How These Truths Inform Our Thesis */}
      <section className="py-32 bg-gradient-to-b from-[#0a1f2e] to-black" data-id="element-1159">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1160">
          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} className="text-center" data-id="element-1161">
            <motion.h2 className="text-4xl md:text-5xl font-bold text-white mb-8" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} data-id="element-1162">
              <MyceliumText as="span" staggerChildren={0.04} data-id="element-1163">
                We're Investing in a World of Vitality
              </MyceliumText>
            </motion.h2>

            <RevealWrapper delay={0.2} data-id="element-1164">
              <p className="text-xl text-gray-300 leading-relaxed mb-6 max-w-3xl mx-auto" data-id="element-1165">
                The world doesn't need more incremental solutions. It needs a
                different way of thinking.
              </p>
              <p className="text-xl text-gray-300 leading-relaxed mb-16 max-w-3xl mx-auto" data-id="element-1166">
                Vitality exists to fund that shift—and we're backing founders
                who follow these three truths.
              </p>
            </RevealWrapper>

            <StaggerContainer className="grid md:grid-cols-3 gap-12 mb-16" staggerDelay={0.15} data-id="element-1167">
              {[{
              title: 'Endurance',
              desc: 'Investing in the companies built to last',
              icon: '1'
            }, {
              title: 'Regeneration',
              desc: 'Compounding value by mimicking living systems',
              icon: '2'
            }, {
              title: 'Conviction',
              desc: 'Backing founders who build through complexity',
              icon: '3'
            }].map((principle, index) => <StaggerItem key={principle.title} data-id="element-1168">
                  <motion.div className="text-center p-6 rounded-xl" whileHover={{
                backgroundColor: 'rgba(43, 199, 183, 0.05)',
                boxShadow: '0 0 30px rgba(43, 199, 183, 0.15)'
              }} transition={{
                duration: 0.3
              }} data-id="element-1169">
                    <motion.div className="w-16 h-16 rounded-full bg-vitalityTeal/10 border-2 border-vitalityTeal flex items-center justify-center mx-auto mb-4" animate={{
                  boxShadow: ['0 0 0px rgba(43, 199, 183, 0.2)', '0 0 15px rgba(43, 199, 183, 0.4)', '0 0 0px rgba(43, 199, 183, 0.2)']
                }} transition={{
                  duration: 2.5,
                  repeat: Infinity,
                  delay: index * 0.3
                }} data-id="element-1170">
                      <span className="text-vitalityTeal font-bold text-xl" data-id="element-1171">
                        {principle.icon}
                      </span>
                    </motion.div>
                    <h3 className="text-xl font-bold text-white mb-3" data-id="element-1172">
                      {principle.title}
                    </h3>
                    <p className="text-gray-400" data-id="element-1173">{principle.desc}</p>
                  </motion.div>
                </StaggerItem>)}
            </StaggerContainer>

            <RevealWrapper delay={0.5} data-id="element-1174">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-1175">
                <Link to="/approach" data-id="element-1176">
                  <motion.div whileHover={{
                  scale: 1.02,
                  boxShadow: '0 0 30px rgba(43, 199, 183, 0.4)'
                }} transition={{
                  duration: 0.3
                }} className="rounded-lg" data-id="element-1177">
                    <Button variant="primary" data-id="element-1178">
                      Our Approach
                      <ArrowRight className="w-4 h-4 ml-2" data-id="element-1179" />
                    </Button>
                  </motion.div>
                </Link>
                <Link to="/portfolio" data-id="element-1180">
                  <motion.div whileHover={{
                  scale: 1.02,
                  boxShadow: '0 0 20px rgba(43, 199, 183, 0.2)'
                }} transition={{
                  duration: 0.3
                }} className="rounded-lg" data-id="element-1181">
                    <Button variant="outline" data-id="element-1182">View Portfolio</Button>
                  </motion.div>
                </Link>
              </div>
            </RevealWrapper>
          </motion.div>
        </div>
      </section>
    </div>;
}